import React, { FunctionComponent } from 'react';

import clsx from 'clsx';

import { useCommonData } from '@stores/global-data';

import { apiSurveyComplete, apiSurveyDismiss } from '@api/methods';

import { isValueSet } from '@utils/guards';
import { SnackBar } from '@utils/snackbar';

import { Button, ButtonProps } from '@components/common/Button/Button';
import { InformingIcon, InformingIconType } from '@components/common/InformingBlock/InformingIcon';

import styles from './InformingBlock.module.scss';

import { QuizBlock } from '../QuizBlock/QuizBlock';

type TitleElement = 'h1' | 'h2';
type TitleMarginBottom = 16 | 40;
type DescriptionSize = 16 | 20;
type DescriptionColor = 'black' | 'muted';
type DescriptionMarginBottom = 16 | 24;

type Title = {
    text: string;
    element?: TitleElement;
    marginBottom?: TitleMarginBottom;
};
type Buttons = [ButtonProps, ButtonProps | null];
type Description = {
    text: string;
    size?: DescriptionSize;
    color?: DescriptionColor;
    marginBottom?: DescriptionMarginBottom;
};

const titleMarginBottomClassMap = (marginBottom: TitleMarginBottom = 16) =>
    ({
        16: styles.isDefaultMarginBottom,
        40: styles.isLargeMarginBottom,
    }[marginBottom]);

const descriptionSizeClassMap = (size: DescriptionSize = 16) =>
    ({
        16: styles.isDefaultSize,
        20: styles.isLargeSize,
    }[size]);

const descriptionColorClassMap = (color: DescriptionColor = 'muted') =>
    ({
        black: styles.isBlack,
        muted: styles.isMuted,
    }[color]);

const descriptionMarginBottomClassMap = (marginBottom: DescriptionMarginBottom = 24) =>
    ({
        24: styles.isDefaultMarginBottom,
        16: styles.isSmallMarginBottom,
    }[marginBottom]);

export type InformingBlockProps = {
    icon: InformingIconType;
    title: Title;
    buttons?: Buttons;
    description?: Description;
    className?: string;
    isAnimatedEnter?: boolean;
    showQuiz?: boolean;
};
export const InformingBlock: FunctionComponent<InformingBlockProps> = (props) => {
    const {
        icon,
        title,
        description,
        buttons,
        className,
        children,
        showQuiz = false,
        isAnimatedEnter = true,
    } = props;
    const {
        text: titleText,
        element: titleElement = 'h2',
        marginBottom: titleMarginBottom = 16,
    } = title;

    const {
        settings: { show_survey_banner, survey_form_url },
    } = useCommonData();

    const TitleTemplate = () =>
        React.createElement(
            titleElement,
            { className: clsx(styles.title, titleMarginBottomClassMap(titleMarginBottom)) },
            titleText,
        );

    return (
        <div className={clsx(styles.root, isAnimatedEnter && styles.isAnimatedEnter, className)}>
            <div className={styles.icon}>
                <InformingIcon icon={icon} />
            </div>

            <TitleTemplate />

            {isValueSet(children) ? <div className={styles.children}>{children}</div> : null}

            {showQuiz && show_survey_banner && survey_form_url !== null && (
                <QuizBlock
                    className={styles.quizBlock}
                    link={survey_form_url}
                    heading="Хотим сделать сервис безупречным!"
                    description={
                        <>
                            Ответьте на&nbsp;пару вопросов о&nbsp;нашем обслуживании&nbsp;— это
                            поможет нам стать лучше
                        </>
                    }
                    onComplete={() => apiSurveyComplete({}).catch(SnackBar.handleApiError)}
                    onDismiss={() => apiSurveyDismiss({}).catch(SnackBar.handleApiError)}
                />
            )}

            {isValueSet(description) ? (
                <p
                    className={clsx(
                        styles.description,
                        descriptionSizeClassMap(description?.size),
                        descriptionColorClassMap(description?.color),
                        descriptionMarginBottomClassMap(description?.marginBottom),
                    )}
                >
                    {description?.text}
                </p>
            ) : null}

            {buttons !== undefined && (
                <div
                    className={clsx(
                        styles.buttonsGroup,
                        isValueSet(buttons[1]) && styles.hasSecondButton,
                    )}
                >
                    {buttons.map((button, index) =>
                        isValueSet(button) ? (
                            <Button key={index} type="button" {...button} />
                        ) : null,
                    )}
                </div>
            )}
        </div>
    );
};
